import type { ReactElement, ReactNode } from "react";
import { defineHFComponent } from "../component.js";
import { defineBrowserSetup } from "../runtime.js";

export type TestimonialProofCardProps = {
  quote?: string;
  name?: string;
  role?: string;
  company?: string;
  emphasis?: string;
  accent?: "green" | "blue" | "violet";
  exit?: "none" | "fade" | "up";
  duration?: number;
  avatar?: ReactNode;
};

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => Array.from(part)[0]?.toUpperCase() ?? "").join("");
}

function View({ name = "Maya Chen", avatar }: TestimonialProofCardProps): ReactElement {
  return <div className="tq-clip"><figure className="tq-card" style={{ margin: 0 }}><div className="tq-glyph" aria-hidden="true">“</div><blockquote className="tq-quote" style={{ margin: 0 }}/><figcaption className="tq-byline"><div className="tq-avatar" data-slot="avatar">{avatar ?? <span className="tq-initials">{initials(name)}</span>}</div><div className="tq-person"><span className="tq-name"/><span className="tq-role"/></div><span className="tq-company"/></figcaption></figure></div>;
}

export const TestimonialProofCard = defineHFComponent<TestimonialProofCardProps>({
  name: "testimonial-proof-card",
  View,
  css: (scope) => `
    ${scope}{position:absolute;inset:0;overflow:hidden;container-type:size;isolation:isolate;display:grid;place-items:center;background:var(--bg,#0b0c0e);color:var(--fg,#f8fafc);font-family:var(--font-display,Inter,system-ui,sans-serif);pointer-events:none}
    ${scope} .tq-clip{position:absolute;inset:0;display:grid;place-items:center;overflow:hidden}
    ${scope} .tq-card{width:min(76cqw,150cqh);padding:6cqmin 7cqmin;border-radius:var(--radius,24px);border:1px solid color-mix(in srgb,var(--border,#343a46) 60%,transparent);background:color-mix(in srgb,var(--surface,#14171c) 88%,transparent);will-change:transform,opacity}
    ${scope} .tq-glyph{font-size:9cqmin;line-height:.6;font-weight:600;color:var(--tq-accent,var(--brand,#71f5a7));margin-bottom:2.4cqmin}
    ${scope} .tq-quote{margin:0;font-size:var(--tq-font-size,5cqmin);font-weight:600;line-height:1.28;letter-spacing:-.02em;text-wrap:balance}
    ${scope} .tq-line-mask{overflow:hidden;padding:.12em .24em .3em;margin:-.12em -.24em -.3em}
    ${scope} .tq-line{will-change:transform,opacity}
    ${scope} .tq-em{position:relative;display:inline-block;white-space:nowrap}
    ${scope} .tq-em-text{position:relative;z-index:1}
    ${scope} .tq-mark{position:absolute;left:-2%;bottom:-.14em;width:104%;height:.24em;overflow:visible;z-index:2}
    ${scope} .tq-byline{display:flex;align-items:center;gap:3cqmin;margin-top:5cqmin;will-change:transform,opacity}
    ${scope} .tq-avatar{width:10cqmin;height:10cqmin;flex:none;border-radius:50%;overflow:hidden;display:grid;place-items:center;border:1px solid color-mix(in srgb,var(--border,#343a46) 70%,transparent);background:color-mix(in srgb,var(--tq-accent,#71f5a7) 16%,var(--surface,#14171c))}
    ${scope} .tq-avatar img,${scope} .tq-avatar video{width:100%;height:100%;object-fit:cover}
    ${scope} .tq-initials{font-family:var(--font-mono,ui-monospace,"SF Mono",Menlo,Consolas,monospace);font-size:3.4cqmin;font-weight:600;letter-spacing:.08em;color:var(--fg,#f8fafc)}
    ${scope} .tq-person{display:grid;gap:.8cqmin;font-family:var(--font-mono,ui-monospace,"SF Mono",Menlo,Consolas,monospace)}
    ${scope} .tq-name{font-size:2.9cqmin;font-weight:600;letter-spacing:.02em}
    ${scope} .tq-role{font-size:2.4cqmin;color:var(--muted,#94a3b8)}
    ${scope} .tq-company{margin-left:auto;font-family:var(--font-mono,ui-monospace,"SF Mono",Menlo,Consolas,monospace);font-size:2.3cqmin;letter-spacing:.3em;text-transform:uppercase;color:var(--muted,#94a3b8)}
  `,
  setup: defineBrowserSetup(
    ({
      quote = "We shipped our launch video in a single afternoon",
      name = "Maya Chen",
      role = "Head of Product",
      company = "NORTHWIND",
      emphasis = "single afternoon",
      accent = "green",
      exit = "none",
      duration = 4.5,
    }) => ({ quote, name, role, company, emphasis, accent, exit, duration: Math.max(0.001, duration) }),
    ({ root, props, tl, gsap, offset }) => {
      const card = root.querySelector(".tq-card");
      const quoteElement = root.querySelector(".tq-quote");
      const byline = root.querySelector(".tq-byline");
      const nameElement = root.querySelector(".tq-name");
      const roleElement = root.querySelector(".tq-role");
      const companyElement = root.querySelector(".tq-company");
      if (!card || !quoteElement || !byline || !nameElement || !roleElement || !companyElement) throw new Error("TestimonialProofCard markup is incomplete");

      const clean = (value: string) => String(value).trim().replace(/\s+/g, " ");
      const quote = clean(props.quote);
      const name = clean(props.name);
      const role = clean(props.role);
      const company = clean(props.company);
      const emphasis = clean(props.emphasis);
      const colors = { green: "#71f5a7", blue: "#61a8ff", violet: "#c5a3ff" };
      const accent = colors[props.accent] || colors.green;
      root.style.setProperty("--tq-accent", accent);
      nameElement.textContent = name;
      if (role) roleElement.textContent = role;
      else roleElement.setAttribute("hidden", "");
      if (company) companyElement.textContent = company;
      else companyElement.setAttribute("hidden", "");

      const initialsElement = root.querySelector(".tq-avatar .tq-initials");
      if (initialsElement) initialsElement.textContent = name.split(" ").filter(Boolean).slice(0, 2).map((part) => Array.from(part)[0]?.toUpperCase() || "").join("");

      let matchStart = -1;
      let matchEnd = -1;
      if (emphasis) {
        const index = quote.toLowerCase().indexOf(emphasis.toLowerCase());
        if (index >= 0) {
          matchStart = quote.lastIndexOf(" ", index) + 1;
          matchEnd = quote.indexOf(" ", index + emphasis.length);
          if (matchEnd < 0) matchEnd = quote.length;
        }
      }

      const characters = Math.max(1, Array.from(quote).length);
      const fitted = Math.max(3.8, Math.min(7.2, 46 / Math.sqrt(characters)));
      root.style.setProperty("--tq-font-size", `${fitted.toFixed(3)}cqmin`);
      const tokens: Element[] = [];
      const pushWords = (text: string) => {
        text.split(" ").forEach((word) => {
          if (!word) return;
          const span = document.createElement("span");
          span.style.display = "inline-block";
          span.textContent = word;
          tokens.push(span);
        });
      };
      let path: SVGPathElement | null = null;
      if (matchStart >= 0) {
        pushWords(quote.slice(0, matchStart));
        const emphasisElement = document.createElement("span");
        emphasisElement.className = "tq-em";
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("class", "tq-mark");
        svg.setAttribute("viewBox", "0 0 100 12");
        svg.setAttribute("preserveAspectRatio", "none");
        svg.setAttribute("aria-hidden", "true");
        path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", "M 2 7 C 20 4.5, 46 8.5, 66 6 C 80 4.5, 92 7.5, 98 5.5");
        path.setAttribute("fill", "none");
        path.setAttribute("stroke-width", "4.5");
        path.setAttribute("stroke-linecap", "round");
        path.setAttribute("stroke-linejoin", "round");
        path.style.stroke = accent;
        svg.appendChild(path);
        const emphasisText = document.createElement("span");
        emphasisText.className = "tq-em-text";
        emphasisText.textContent = quote.slice(matchStart, matchEnd);
        emphasisElement.appendChild(svg);
        emphasisElement.appendChild(emphasisText);
        tokens.push(emphasisElement);
        pushWords(quote.slice(matchEnd));
      } else pushWords(quote);
      tokens.forEach((token, index) => {
        if (index > 0) quoteElement.appendChild(document.createTextNode(" "));
        quoteElement.appendChild(token);
      });
      quoteElement.setAttribute("aria-label", quote);

      const rows: Element[][] = [];
      let lastTop: number | null = null;
      tokens.forEach((token) => {
        const top = (token as HTMLElement).offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 2) {
          rows.push([]);
          lastTop = top;
        }
        rows[rows.length - 1]!.push(token);
      });
      quoteElement.textContent = "";
      const lines = rows.map((row) => {
        const mask = document.createElement("div");
        mask.className = "tq-line-mask";
        const line = document.createElement("div");
        line.className = "tq-line";
        row.forEach((token, index) => {
          if (index > 0) line.appendChild(document.createTextNode(" "));
          line.appendChild(token);
        });
        mask.appendChild(line);
        quoteElement.appendChild(mask);
        return line;
      });

      const cardBase = 0.5;
      const lineBase = 0.6;
      const staggerBase = 0.18;
      const linesAtBase = 0.15;
      const bylineBase = 0.5;
      const drawBase = 0.55;
      const outBase = props.exit === "none" ? 0 : 0.45;
      const quoteEndBase = linesAtBase + Math.max(0, lines.length - 1) * staggerBase + lineBase;
      const bylineAtBase = Math.max(0, quoteEndBase - 0.15);
      const drawAtBase = quoteEndBase + 0.1;
      const inEndBase = Math.max(cardBase, bylineAtBase + bylineBase, path ? drawAtBase + drawBase : 0);
      const scale = props.duration < inEndBase + outBase ? props.duration / (inEndBase + outBase) : 1;
      const outro = outBase * scale;
      const outStart = props.duration - outro;

      gsap.set(card, { opacity: 0, y: "4cqh" });
      gsap.set(lines, { yPercent: 130, opacity: 0 });
      gsap.set(byline, { opacity: 0, y: "2.5cqh" });
      tl.fromTo(card, { opacity: 0, y: "4cqh" }, { opacity: 1, y: "0cqh", duration: cardBase * scale, ease: "power3.out" }, offset);
      lines.forEach((line, index) => tl.fromTo(line, { yPercent: 130, opacity: 0 }, { yPercent: 0, opacity: 1, duration: lineBase * scale, ease: "power3.out" }, offset + (linesAtBase + index * staggerBase) * scale));
      tl.fromTo(byline, { opacity: 0, y: "2.5cqh" }, { opacity: 1, y: "0cqh", duration: bylineBase * scale, ease: "power3.out" }, offset + bylineAtBase * scale);
      if (path) {
        const length = path.getTotalLength();
        path.style.strokeDasharray = String(length);
        gsap.set(path, { strokeDashoffset: length, opacity: 0 });
        tl.set(path, { opacity: 1 }, offset + drawAtBase * scale);
        tl.fromTo(path, { strokeDashoffset: length }, { strokeDashoffset: 0, duration: drawBase * scale, ease: "power2.inOut" }, offset + drawAtBase * scale);
      }
      if (props.exit === "fade") tl.to(card, { opacity: 0, duration: outro, ease: "power2.in" }, offset + outStart);
      else if (props.exit === "up") tl.to(card, { opacity: 0, y: "-6cqh", duration: outro, ease: "power2.in" }, offset + outStart);
    },
  ),
});
